import { createBox, createText } from '@shopify/restyle';
import type { TextStyle } from 'react-native';

import type { THEME } from '../generated/theme';

interface Breakpoints {
  small: number;
  medium: number;
  large: number;
  [key: string]: number;
}

type DefaultSlice = typeof THEME.light.small;
type ThemeColorToken = keyof DefaultSlice['colors'];
type ThemeResponsiveToken = keyof DefaultSlice['responsive'];
type ThemeTypographyToken =
  | Extract<ThemeResponsiveToken, `typography.${string}`>
  | `typography.font-family.${'display' | 'header' | 'body' | 'body-medium' | 'body-bold' | 'body-light'}`;
type ThemeSizingToken = Extract<ThemeResponsiveToken, `sizing.${string}`>;
type ThemeSpacingToken = Extract<ThemeResponsiveToken, `spacing.${string}`>;
type ThemeRadiusToken = Extract<ThemeResponsiveToken, `radius.${string}`>;
type ThemeBorderWidthToken = Extract<ThemeResponsiveToken, `border-width.${string}`>;

interface TextVariant {
  fontFamily?: string;
  fontSize?: number;
  lineHeight?: number;
  letterSpacing?: number;
  fontWeight?: TextStyle['fontWeight'];
  color?: string;
  textDecorationLine?: 'underline' | 'none';
}

type ViewVariant = Record<string, string | number | undefined>;
type TextVariantToken =
  | 'defaults'
  | 'headerDisplay2'
  | 'headerDisplay1'
  | 'headerLarge'
  | 'headerMedium'
  | 'headerSmall'
  | 'headerXSmall'
  | 'bodyXLarge'
  | 'bodyXLargeMedium'
  | 'bodyXLargeStrong'
  | 'bodyLarge'
  | 'bodyLargeMedium'
  | 'bodyLargeStrong'
  | 'bodyBase'
  | 'bodyBaseMedium'
  | 'bodyBaseStrong'
  | 'bodySmall'
  | 'bodySmallMedium'
  | 'bodySmallStrong'
  | 'bodyXSmall'
  | 'bodyXSmallMedium'
  | 'bodyXSmallStrong'
  | 'bodyCaption'
  | 'bodyCaptionMedium'
  | 'bodyCaptionStrong'
  | 'link'
  | 'linkLarge'
  | 'linkSmall';

export interface Theme {
  colors: Record<ThemeColorToken, string>;
  spacing: Record<ThemeSpacingToken, number>;
  borderRadii: Record<ThemeRadiusToken, number>;
  borderWidths: Record<ThemeBorderWidthToken, number>;
  breakpoints: Breakpoints;
  textVariants: Record<TextVariantToken, TextVariant>;
  buttonVariants: Record<string, ViewVariant>;
  tokens: {
    typography: Record<ThemeTypographyToken, string>;
    sizing: Record<ThemeSizingToken, string>;
    spacing: Record<ThemeSpacingToken, string>;
    radius: Record<ThemeRadiusToken, string>;
    borderWidth: Record<ThemeBorderWidthToken, string>;
  };
  fonts: Record<'inter' | 'theFuture', Record<string, string>> & Record<string, Record<string, string>>;
}

export const Box = createBox<Theme>();
export const Text = createText<Theme>();
